if (!customElements.get('brand-logos')) {
  class BrandLogos extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.selectors = {
        sliderWrapper: '.brand-logos__items',
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
        scrollbarEl: '.swiper-scrollbar',
      };

      this.classes = {
        grid: 'f-grid',
        swiper: 'swiper',
        swiperWrapper: 'swiper-wrapper',
        swiperSlide: 'swiper-slide',
      };

      this.sectionId = this.dataset.sectionId;
      this.section = this.closest(`#section-${this.sectionId}`) || this;
      this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);

      this.columnsDesktop = Number(this.dataset.columnsDesktop || 0);
      this.columnsLaptop = Number(this.dataset.columnsLaptop || 0);
      this.columnsTablet = Number(this.dataset.columnsTablet || 0);
      this.columnsMobile = Number(this.dataset.columnsMobile || 0);

      this.sliderInstance = false;

      const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
      mql.onchange = this.init.bind(this);

      // Defer Swiper initialization until the section is near the viewport.
      // The brand marquee looks identical, but its setup cost (and continuous
      // autoplay) is moved out of the initial main-thread work window, which
      // improves TBT/TTI. Mirrors the FoxTheme.Motion.inView lazy-init pattern
      // used by the other slider sections in this theme.
      if (FoxTheme.Motion && FoxTheme.Motion.inView) {
        FoxTheme.Motion.inView(this, this.init.bind(this), { margin: '600px 0px 600px 0px' });
      } else {
        this.init();
      }
    }

    init() {
      // This section is a "slider" section; keep Swiper enabled on mobile too.
      // We rely on Swiper breakpoints for responsive behavior.
      if (typeof this.sliderInstance !== 'object') {
        this.initSlider();
        return;
      }

      if (this.sliderInstance?.slider && typeof this.sliderInstance.slider.update === 'function') {
        this.sliderInstance.slider.update();
      }
    }

    initSlider() {
      if (!this.sliderWrapper) return;

      const scrollbarEl = this.section.querySelector(this.selectors.scrollbarEl);

      const nextEl = this.section.querySelector(this.selectors.nextEl);
      const prevEl = this.section.querySelector(this.selectors.prevEl);

      const modules = [FoxTheme.Swiper.Mousewheel];
      if (nextEl && prevEl && FoxTheme.Swiper.Navigation) modules.push(FoxTheme.Swiper.Navigation);
      if (FoxTheme.Swiper.Pagination) modules.push(FoxTheme.Swiper.Pagination);
      if (scrollbarEl && FoxTheme.Swiper.Scrollbar) modules.push(FoxTheme.Swiper.Scrollbar);
      // Swiper requires the Autoplay module to be registered, otherwise the `autoplay` option is ignored.
      if (FoxTheme.Swiper.Autoplay) modules.push(FoxTheme.Swiper.Autoplay);

      const sliderOptions = {
        modules,
        slidesPerView: this.columnsMobile > 0 ? this.columnsMobile : 'auto',
        spaceBetween: 60,
        speed: 6000,
        ...(nextEl && prevEl
          ? {
            navigation: {
              nextEl,
              prevEl,
            },
          }
          : {}),
        ...(scrollbarEl && FoxTheme.Swiper.Scrollbar
          ? {
            scrollbar: {
              el: scrollbarEl,
              draggable: true,
            },
          }
          : {}),
        pagination: false,
        threshold: 2,
        autoplay: {
          delay: 0,
          disableOnInteraction: false,
          pauseOnMouseEnter: false,
          waitForTransition: false,
        },
        loop: true,
        mousewheel: {
          enabled: true,
          forceToAxis: true,
        },
        breakpoints: {
          768: {
            slidesPerView: this.columnsTablet > 0 ? this.columnsTablet : 'auto',
          },
          1024: {
            slidesPerView: this.columnsLaptop > 0 ? this.columnsLaptop : 'auto',
          },
          1280: {
            slidesPerView: this.columnsDesktop > 0 ? this.columnsDesktop : 'auto',
          },
        },
      };

      if (typeof this.sliderInstance !== 'object') {
        // Ensure slides have swiper-slide class (required by Swiper).
        [...this.sliderWrapper.children].forEach((child) => {
          if (!child.classList?.contains(this.classes.swiperSlide)) child.classList.add(this.classes.swiperSlide);
        });

        // Remove swipe-mobile overflow/grid behavior (it uses !important and overrides Swiper).
        // If JS fails to load, the markup still has swipe-mobile classes and will fall back to native scrolling.
        if (this.classList.contains('swipe-mobile')) {
          this.classList.remove('swipe-mobile');
          [...this.classList].forEach((c) => {
            if (c.indexOf('swipe-mobile--') === 0) this.classList.remove(c);
          });
        }
        if (this.sliderWrapper?.classList?.contains('swipe-mobile__inner')) {
          this.sliderWrapper.classList.remove('swipe-mobile__inner');
        }

        this.classList.add(this.classes.swiper);
        this.sliderWrapper.classList.remove(this.classes.grid);
        this.sliderWrapper.classList.add(this.classes.swiperWrapper);

        this.sliderInstance = new window.FoxTheme.Carousel(this, sliderOptions);
        this.sliderInstance.init();

        const focusableElements = FoxTheme.a11y.getFocusableElements(this);

        focusableElements.forEach((element) => {
          element.addEventListener('focusin', () => {
            const slide = element.closest('.swiper-slide');
            if (!slide) return;
            this.sliderInstance.slider.slideTo(this.sliderInstance.slider.slides.indexOf(slide));
          });
        });

        if (Shopify.designMode) {
          document.addEventListener('shopify:block:select', (e) => {
            if (e.detail.sectionId != this.sectionId) return;
            let { target } = e;
            const index = Number(target.dataset.index);

            this.sliderInstance.slider.slideTo(index);
          });
        }
      }
    }

    destroySlider() {
      this.classList.remove(this.classes.swiper);
      this.sliderWrapper.classList.remove(this.classes.swiperWrapper);
      this.sliderWrapper.classList.add(this.classes.grid);
      if (typeof this.sliderInstance !== 'object') return;
      this.sliderInstance.slider.destroy();
      this.sliderInstance = false;
    }
  }
  customElements.define('brand-logos', BrandLogos);
}


